/**
 * ImageVersionHistorySheet Component
 *
 * Sheet UI for image version history and rollback.
 * Adapted from VersionHistoryOffcanvas for images.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { History, X, RotateCcw } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/v2/ui';
import { Button } from '@/components/v2/ui';
import { Badge } from '@/components/v2/ui';
import { getImageVersions, rollbackImageVersion } from '@/lib/api/image-collection';
import type { ImageVersion } from '@/types/image-collection';

interface ImageVersionHistorySheetProps {
  productId: string;
  linkId: string;
  assetId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onRollbackSuccess?: () => void;
}

export function ImageVersionHistorySheet({
  productId,
  linkId,
  isOpen,
  onClose,
  onRollbackSuccess,
}: ImageVersionHistorySheetProps) {
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Load version history
  const versionsQuery = useQuery({
    queryKey: ['image-versions', productId, linkId],
    queryFn: () => getImageVersions(productId, linkId),
    enabled: isOpen && !!productId && !!linkId,
  });

  // Rollback mutation
  const rollbackMutation = useMutation({
    mutationFn: (versionId: string) => rollbackImageVersion(productId, linkId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['image-versions', productId, linkId] });
      queryClient.invalidateQueries({ queryKey: ['image-collection', productId] });
      onRollbackSuccess?.();
      setCompareVersionId(null);
    },
  });

  const handleRollback = (versionId: string, versionNumber: number) => {
    if (confirm(`Restore to version ${versionNumber}? This will create a new version.`)) {
      rollbackMutation.mutate(versionId);
    }
  };

  const versions = versionsQuery.data?.versions || [];
  const link = versionsQuery.data?.link;
  const currentVersion = versions.find((v) => v.id === link?.current_version_id);
  const compareVersion = versions.find((v) => v.id === compareVersionId);

  // Group versions into Current and History
  const historicalVersions = versions.filter((v) => v.id !== link?.current_version_id);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg overflow-y-auto">
        <SheetHeader className="border-b border-neutral-200 dark:border-neutral-700 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Version History
          </SheetTitle>
          <SheetDescription>
            View and restore previous versions
          </SheetDescription>
        </SheetHeader>

        <div className="py-4">
          {/* Loading */}
          {versionsQuery.isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-neutral-900 dark:border-neutral-50" />
            </div>
          )}

          {/* Error */}
          {versionsQuery.error && (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to load version history
              </p>
            </div>
          )}

          {/* Comparison View */}
          {compareVersionId && currentVersion && compareVersion && (
            <div className="mb-6 space-y-4">
              <div className="flex justify-between items-center">
                <h6 className="text-sm font-medium text-neutral-900 dark:text-neutral-50">
                  Comparison: V{compareVersion.version_number} → V{currentVersion.version_number}
                </h6>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCompareVersionId(null)}
                  aria-label="Close comparison"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Side-by-side images */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                    V{compareVersion.version_number} (Old)
                  </p>
                  <img
                    src={`/api/v1/images/${compareVersion.assets?.s3_key_original}/THUMB?fallback=true`}
                    alt={`Version ${compareVersion.version_number}`}
                    className="w-full rounded border border-neutral-200 dark:border-neutral-700"
                  />
                  <div className="mt-2 text-xs text-neutral-600 dark:text-neutral-400">
                    {compareVersion.assets?.width} × {compareVersion.assets?.height}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                    V{currentVersion.version_number} (Current)
                  </p>
                  <img
                    src={`/api/v1/images/${currentVersion.assets?.s3_key_original}/THUMB?fallback=true`}
                    alt={`Version ${currentVersion.version_number}`}
                    className="w-full rounded border border-neutral-200 dark:border-neutral-700"
                  />
                  <div className="mt-2 text-xs text-neutral-600 dark:text-neutral-400">
                    {currentVersion.assets?.width} × {currentVersion.assets?.height}
                  </div>
                </div>
              </div>

              <Button
                onClick={() => handleRollback(compareVersion.id, compareVersion.version_number)}
                disabled={rollbackMutation.isPending}
                className="w-full"
              >
                {rollbackMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Restore V{compareVersion.version_number}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Versions List */}
          {!versionsQuery.isLoading && !versionsQuery.error && (
            <div className="space-y-6">
              {/* Current Version */}
              {currentVersion && (
                <div>
                  <h6 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
                    Current
                  </h6>
                  <VersionCard
                    version={currentVersion}
                    isCurrent={true}
                    onCompare={() => {}}
                    onRestore={() => {}}
                  />
                </div>
              )}

              {/* Historical Versions */}
              {historicalVersions.length > 0 && (
                <div>
                  <h6 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
                    History
                  </h6>
                  <div className="space-y-3">
                    {historicalVersions.map((version) => (
                      <VersionCard
                        key={version.id}
                        version={version}
                        isCurrent={false}
                        onCompare={() => setCompareVersionId(version.id)}
                        onRestore={() => handleRollback(version.id, version.version_number)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {versions.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    No version history available
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * VersionCard Component (inline, simplified for images)
 */
function VersionCard({
  version,
  isCurrent,
  onCompare,
  onRestore,
}: {
  version: ImageVersion;
  isCurrent: boolean;
  onCompare: () => void;
  onRestore: () => void;
}) {
  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-3">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium text-neutral-900 dark:text-neutral-50">
            V{version.version_number}
          </span>
          {isCurrent && (
            <Badge className="bg-green-500 text-white text-xs">Current</Badge>
          )}
          {version.status === 'archived' && (
            <Badge className="bg-neutral-500 text-white text-xs">Archived</Badge>
          )}
        </div>
        <div className="flex gap-1">
          {!isCurrent && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onCompare}
                className="h-7 px-2 text-xs"
              >
                Compare
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onRestore}
                className="h-7 px-2 text-xs"
              >
                <RotateCcw className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Thumbnail */}
      {version.assets?.s3_key_original && (
        <img
          src={`/api/v1/images/${version.assets.s3_key_original}/THUMB?fallback=true`}
          alt={`Version ${version.version_number}`}
          className="w-[180px] h-[180px] object-cover rounded mb-2"
        />
      )}

      {/* Metadata */}
      <div className="space-y-1 text-xs text-neutral-600 dark:text-neutral-400">
        <div className="flex justify-between">
          <span>Source:</span>
          <span className="font-medium">{version.source_type}</span>
        </div>
        {version.assets && (
          <div className="flex justify-between">
            <span>Dimensions:</span>
            <span className="font-medium">
              {version.assets.width} × {version.assets.height}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span>Created:</span>
          <span className="font-medium">
            {new Date(version.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  );
}
